import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Camera, Refrigerator, Scan, Clock, Users, ArrowLeft, Sparkles, X, Loader2, Image as ImageIcon, Check, AlertCircle, ImagePlus, Globe, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import GustoLoadingOverlay from "@/components/GustoLoadingOverlay";
import { motion, AnimatePresence } from "framer-motion";
import ChefAvatar from "@/components/ChefAvatar";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const CATEGORY_EMOJIS = {
  legume: "🥬",
  fruit: "🍎",
  viande: "🥩",
  poisson: "🐟",
  produit_laitier: "🥛",
  oeuf: "🥚",
  fromage: "🧀",
  condiment: "🧂",
  boisson: "🥤",
  autre: "🍳"
};

const FridgeScannerPage = () => {
  const navigate = useNavigate();
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [chefGustoRecipes, setChefGustoRecipes] = useState([]);
  const [loadingChefGusto, setLoadingChefGusto] = useState(false);
  const [chefGustoAsked, setChefGustoAsked] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const triggerCamera = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
      cameraInputRef.current.click();
    }
  };

  const triggerGallery = () => {
    if (galleryInputRef.current) {
      galleryInputRef.current.value = '';
      galleryInputRef.current.click();
    }
  };
  
  // Ask Chef Gusto to generate recipes based on detected ingredients
  const askChefGusto = async () => {
    if (!results?.detected_ingredients || results.detected_ingredients.length === 0) {
      toast.error("Aucun ingrédient détecté pour générer des recettes");
      return;
    }
    
    setLoadingChefGusto(true);
    setChefGustoAsked(true);
    try {
      const ingredientNames = results.detected_ingredients.map(ing => ing.name);
      const response = await axios.post(`${API}/recipes/search-web`, {
        ingredients: ingredientNames,
        limit: 3
      });
      
      if (response.data.recipes && response.data.recipes.length > 0) {
        setChefGustoRecipes(response.data.recipes);
        toast.success(`Chef Gusto vous propose ${response.data.recipes.length} recettes !`);
      } else {
        toast.info("Chef Gusto n'a pas trouvé de recettes pour ces ingrédients");
      }
    } catch (err) {
      console.error("Chef Gusto error:", err);
      toast.error("Erreur lors de la génération des recettes");
    } finally {
      setLoadingChefGusto(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image trop grande (max 10MB)");
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result);
      setImagePreview(reader.result);
      setResults(null);
      setError(null);
    };
    reader.onerror = () => {
      toast.error("Erreur lors de la lecture de l'image");
    };
    reader.readAsDataURL(file);
  };

  const analyzeImage = async () => {
    if (!image) {
      toast.error("Prenez une photo de votre frigo d'abord");
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/fridge/analyze`, {
        image_base64: image
      });

      setResults(response.data);
      
      if (response.data.detected_ingredients.length === 0) {
        toast.info("Aucun ingrédient détecté. Essayez avec une photo plus claire.");
      } else {
        toast.success(`${response.data.detected_ingredients.length} ingrédients détectés !`);
      }
    } catch (err) {
      console.error("Analysis error:", err);
      setError("Erreur lors de l'analyse. Veuillez réessayer.");
      toast.error("Erreur lors de l'analyse");
    } finally {
      setAnalyzing(false);
    }
  };

  const resetScan = () => {
    setImage(null);
    setImagePreview(null);
    setResults(null);
    setError(null);
    setChefGustoRecipes([]);
    setChefGustoAsked(false);
  };
  
  // Navigate to web recipe detail page
  const viewChefGustoRecipe = (recipe) => {
    // Store the recipe in sessionStorage for the detail page
    sessionStorage.setItem('webRecipe', JSON.stringify(recipe));
    navigate(`/web-recipe/${recipe.id}`);
  };

  return (
    <div className="min-h-screen pb-24" data-testid="fridge-scanner-page">
      <GustoLoadingOverlay show={analyzing} message="Chef Gusto analyse vos ingrédients..." />
      <GustoLoadingOverlay show={loadingChefGusto} message="Chef Gusto prépare des recettes..." />
      <div className="bg-gradient-to-br from-cyan-500/20 via-blue-500/10 to-primary/20 px-4 pt-6 pb-8">
        <Link 
          to="/discover" 
          className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </Link>

        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4 shadow-lg">
            <Refrigerator className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-charcoal">
            Scanner Mon Frigo
          </h1>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">
            Prenez une photo de l'intérieur de votre frigo et découvrez les recettes que vous pouvez réaliser !
          </p>
        </div>
      </div>

      <div className="px-4 -mt-4">
        {/* Image Upload Section */}
        {!results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="shadow-lg overflow-hidden">
              <CardContent className="p-0">
                {imagePreview ? (
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Aperçu du frigo" 
                      className="w-full aspect-[4/3] object-cover"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                      onClick={resetScan}
                    >
                      <X className="w-5 h-5" />
                    </Button>
                    
                    {analyzing && (
                      <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
                        <Loader2 className="w-12 h-12 text-white animate-spin mb-4" />
                        <p className="text-white font-medium">Analyse en cours...</p>
                        <p className="text-white/60 text-sm mt-1">Détection des ingrédients</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div 
                    className="aspect-[4/3] flex flex-col items-center justify-center bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={triggerCamera}
                  >
                    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Camera className="w-10 h-10 text-primary" />
                    </div>
                    <p className="font-medium text-charcoal">Prendre une photo</p>
                    <p className="text-sm text-muted-foreground mt-1">ou sélectionner depuis la galerie</p>
                  </div>
                )}

                {/* Camera Input - Opens camera directly on mobile */}
                <input
                  id="fridge-camera-input"
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageChange}
                  className="sr-only"
                />
                
                {/* Gallery Input - Opens photo gallery */}
                <input
                  id="fridge-gallery-input"
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="sr-only"
                />
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="mt-4 space-y-3">
              {!imagePreview ? (
                <div className="flex flex-col gap-3">
                  {/* Primary: Camera Button */}
                  <Button
                    onClick={triggerCamera}
                    className="w-full h-14 rounded-2xl bg-primary hover:bg-primary/90 text-lg gap-2"
                  >
                    <Camera className="w-5 h-5" />
                    Prendre une photo
                  </Button>
                  
                  {/* Secondary: Gallery Button */}
                  <Button
                    variant="outline"
                    onClick={triggerGallery}
                    className="w-full h-12 rounded-2xl gap-2"
                  >
                    <ImagePlus className="w-5 h-5" />
                    Choisir depuis la galerie
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={analyzeImage}
                  disabled={analyzing}
                  className="w-full h-14 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-lg gap-2"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Analyse en cours...
                    </>
                  ) : (
                    <>
                      <Scan className="w-5 h-5" />
                      Analyser les ingrédients
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Tips */}
            <Card className="mt-6 bg-primary/5 border-primary/20">
              <CardContent className="p-4">
                <h3 className="font-medium text-charcoal flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  Conseils pour une meilleure analyse
                </h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Ouvrez bien la porte du frigo</li>
                  <li>• Assurez-vous d'avoir un bon éclairage</li>
                  <li>• Prenez la photo de face</li>
                  <li>• Incluez tous les étages visibles</li>
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Results Section */}
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Detected Ingredients */}
            <Card className="shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    Ingrédients détectés
                  </h2>
                  <Badge className="bg-green-500">{results.detected_ingredients.length}</Badge>
                </div>

                {results.detected_ingredients.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {results.detected_ingredients.map((ing, index) => (
                      <Badge 
                        key={index} 
                        variant="outline"
                        className="py-1.5 px-3 text-sm bg-white border-muted-foreground/20 text-charcoal font-medium"
                      >
                        <span className="mr-1.5">{CATEGORY_EMOJIS[ing.category] || "🍳"}</span>
                        <span className="text-charcoal">{ing.name}</span>
                        {ing.quantity && <span className="ml-1.5 text-muted-foreground">({ing.quantity})</span>}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Aucun ingrédient détecté</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Matching Recipes */}
            <div>
              <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                <GustoIcon className="w-5 h-5" />
                Recettes possibles
                {results.matching_recipes.length > 0 && (
                  <Badge variant="outline">{results.matching_recipes.length}</Badge>
                )}
              </h2>

              {/* Local App Recipes (Priority) */}
              {results.matching_recipes.length > 0 && (
                <div className="space-y-3 mb-6">
                  {results.matching_recipes.map((recipe, index) => (
                    <motion.div
                      key={recipe.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link to={`/recipe/${recipe.id}`}>
                        <Card className="overflow-hidden hover:shadow-md transition-shadow">
                          <div className="flex">
                            {/* Recipe Image */}
                            <div className="w-24 h-24 flex-shrink-0 bg-gradient-to-br from-primary/20 to-accent/20">
                              {recipe.final_image ? (
                                <img 
                                  src={recipe.final_image.startsWith('data:') ? recipe.final_image : `data:image/jpeg;base64,${recipe.final_image}`}
                                  alt={recipe.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <GustoIcon className="w-8 h-8 opacity-40" />
                                </div>
                              )}
                            </div>

                            {/* Recipe Info */}
                            <CardContent className="flex-1 p-3">
                              <h3 className="font-medium text-charcoal line-clamp-1">{recipe.name}</h3>
                              
                              <div className="flex items-center gap-2 mt-1">
                                <ChefAvatar
                                  src={recipe.chef_avatar}
                                  name={recipe.chef_pseudo}
                                  size="xs"
                                  showToque={true}
                                />
                                <span className="text-xs text-muted-foreground">{recipe.chef_pseudo}</span>
                              </div>

                              {/* Match indicator */}
                              <div className="mt-2">
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="text-muted-foreground">
                                    {recipe.matching_ingredients}/{recipe.total_ingredients} ingrédients
                                  </span>
                                  <span className={`font-medium ${
                                    recipe.match_percentage >= 70 ? 'text-green-500' :
                                    recipe.match_percentage >= 40 ? 'text-amber-500' :
                                    'text-red-400'
                                  }`}>
                                    {recipe.match_percentage}%
                                  </span>
                                </div>
                                <Progress 
                                  value={recipe.match_percentage} 
                                  className="h-1.5"
                                />
                              </div>
                            </CardContent>
                          </div>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* No local recipes message */}
              {results.matching_recipes.length === 0 && !chefGustoAsked && (
                <Card className="p-6 text-center mb-4 bg-muted/30">
                  <GustoLogo size="md" className="mx-auto opacity-40 mb-2" />
                  <p className="text-muted-foreground text-sm">
                    Aucune recette de la communauté ne correspond
                  </p>
                </Card>
              )}

              {/* Ask Chef Gusto Button */}
              {!chefGustoAsked && (
                <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-charcoal">Chef Gusto</h3>
                        <p className="text-sm text-muted-foreground">
                          Demandez au Chef Gusto de créer 3 recettes avec vos ingrédients !
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={askChefGusto}
                      disabled={loadingChefGusto}
                      className="w-full mt-4 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                    >
                      {loadingChefGusto ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Chef Gusto réfléchit...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5 mr-2" />
                          Demander au Chef Gusto
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Chef Gusto Loading */}
              {loadingChefGusto && (
                <Card className="p-8 text-center mt-4">
                  <Loader2 className="w-12 h-12 mx-auto text-cyan-500 animate-spin mb-3" />
                  <p className="font-medium text-charcoal">Chef Gusto prépare vos recettes...</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Analyse des ingrédients en cours
                  </p>
                </Card>
              )}

              {/* Chef Gusto Recipes */}
              {chefGustoAsked && !loadingChefGusto && chefGustoRecipes.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-semibold text-charcoal">Recettes du Chef Gusto</span>
                    <Badge className="bg-cyan-500">{chefGustoRecipes.length}</Badge>
                  </div>
                  
                  <div className="space-y-3">
                    {chefGustoRecipes.map((recipe, index) => (
                      <motion.div
                        key={recipe.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Card 
                          className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer border-cyan-200"
                          onClick={() => viewChefGustoRecipe(recipe)}
                        >
                          <div className="flex">
                            <div className="w-24 h-24 flex-shrink-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                              <GustoIcon className="w-10 h-10 opacity-60" />
                            </div>

                            <CardContent className="flex-1 p-3">
                              <h3 className="font-medium text-charcoal line-clamp-1">{recipe.name}</h3>
                              
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs bg-cyan-50 text-cyan-700 border-cyan-200">
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  Chef Gusto
                                </Badge>
                              </div>

                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                {recipe.prep_time && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {recipe.prep_time}
                                  </span>
                                )}
                                {recipe.servings && (
                                  <span className="flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {recipe.servings}
                                  </span>
                                )}
                                {recipe.ingredients?.length > 0 && (
                                  <span>{recipe.ingredients.length} ing.</span>
                                )}
                              </div>
                            </CardContent>
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Chef Gusto no results */}
              {chefGustoAsked && !loadingChefGusto && chefGustoRecipes.length === 0 && (
                <Card className="p-6 text-center mt-4">
                  <GustoLogo size="md" className="mx-auto opacity-40 mb-2" />
                  <p className="text-muted-foreground">
                    Chef Gusto n'a pas pu générer de recettes
                  </p>
                  <Link to="/record">
                    <Button className="mt-4 rounded-full">
                      Créer ma propre recette
                    </Button>
                  </Link>
                </Card>
              )}
            </div>

            {/* Scan Again Button */}
            <Button
              variant="outline"
              onClick={resetScan}
              className="w-full h-12 rounded-2xl gap-2"
            >
              <Camera className="w-5 h-5" />
              Scanner à nouveau
            </Button>
          </motion.div>
        )}

        {/* Error State */}
        {error && (
          <Card className="mt-6 border-destructive bg-destructive/10">
            <CardContent className="p-4 text-center">
              <AlertCircle className="w-8 h-8 mx-auto text-destructive mb-2" />
              <p className="text-destructive font-medium">{error}</p>
              <Button 
                variant="outline" 
                onClick={resetScan}
                className="mt-4 rounded-full"
              >
                Réessayer
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FridgeScannerPage;
